import React, {useState, useEffect} from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom'

import PageTitle from './PageTitle';
import Waiting from './Waiting';
import ErrorDisplay from './ErrorDisplay';
import Invoice from './Invoice';
import PayPalButton from './PayPalButton';
import InvoicePdf from './InvoicePdf';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';

import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import FormHelperText from '@mui/material/FormHelperText';

import useMediaQuery from '@mui/material/useMediaQuery';
import clsx from 'clsx';
import useStyles from '../styles/Styles';

import _ from 'lodash';
import { isTrue, includesSubscription } from '../utils';

import { checkout, loadSettings } from '../actions';

const Checkout = () => {
	const classes = useStyles();
	const isSm = useMediaQuery('(min-width:650px)');
	const isMd = useMediaQuery('(min-width:960px)');
	const flexLayout = useMediaQuery('(max-width:750px)');
	
	const dispatch = useDispatch();
	const permissions = useSelector(state => state.permissions);
	//const user = useSelector(state => state.user);
	const checkoutRequest = useSelector(state => state.requests.checkout);
	const cart = useSelector(state => state.cart.data);
	const generalSettings = useSelector(state => state.settings.general.data);
	const invoiceSettings = useSelector(state => state.settings.invoices.data);
	const invoices = useSelector(state => state.invoices.data, _.isEqual);
	const memberSettings = useSelector(state => state.settings.members.data);
	
	const navigate = useNavigate();
	
	useEffect(() => {
		dispatch(loadSettings('invoices'));
		dispatch(loadSettings('members'));
	}, [dispatch])
	
	const [submitted, setSubmitted] = useState(false);
	const [invoice, setInvoice] = useState({});
	const [termsAgreed, setTermsAgreed] = useState(false);
	const [termsRequired, setTermsRequired] = useState(true);
	
	useEffect(() => {
		if(checkoutRequest.submitting){		
			setSubmitted(true);
		} else if(submitted && !_.isEmpty(invoices[checkoutRequest.invoice])){
			setInvoice(invoices[checkoutRequest.invoice]);
		} 
	}, [checkoutRequest, invoices, submitted])
	
	useEffect(() => {
		if(!isTrue(memberSettings.show_terms_and_conditions) || !includesSubscription(cart)){
			setTermsRequired(false);
		} else {
			setTermsRequired(true);
		}
	},[memberSettings, cart])
	
	const dispatchCheckout = (termsAgreed) => {
		dispatch(checkout(termsAgreed));
		setSubmitted(true);
	}
	
	const CheckoutContent = () => {
		if(checkoutRequest.submitting){
			return <Waiting message='Checking out. Please wait...' />
		} else if(submitted){
			
			if(!_.isEmpty(invoice)){

				return <>
				<Typography variant='h5' component='p' gutterBottom>Checkout success</Typography>
				<Typography variant='body1' gutterBottom>Total due is ${_.toNumber(invoice.due).toFixed(2)}.</Typography>
				
				{invoice.due > 0 ? 
				<Typography variant='body1' paragraph>Available payment methods are shown at the bottom of the invoice below.</Typography> : 
				<Typography variant='body1' paragraph>Checkout is complete.</Typography>}
				
				<div className={classes.formPPButtons}>
				<InvoicePdf 
					buttonSize={isSm ? 'medium' : 'small'}
					invoiceId={invoice.invoice_no}
				/>
				{invoiceSettings.paypal.accept && invoice.due > 0 ?
				<PayPalButton 
					invoiceNo={invoice.invoice_no}
				/>
				: null}
				</div>
				
				<Paper className={classes.page} 
					sx={{
						marginTop: 2
					}}
					square={true} elevation={2}
				>
				<div className={classes.invoiceDetail}>
				<Invoice 
					invoiceId={invoice.invoice_no}
					withPaper={true}
					isSm={isSm}
					isMd={isMd}
					flexLayout={flexLayout}
					buttonTop={true}
				/>
				</div>
				</Paper>
				
				</>
			} else {
				return null;
			}
		} else if(cart.length === 0) {
			return <Typography paragraph>
			Nothing to check out: Your cart is empty.
			</Typography>
		} else {
			const items = _.chain(cart).map(c => {
				return c.course_code + ': ' + c.course_name
			}).join('\n').value();
			
			return <>
			<Typography variant='h5' component='p' gutterBottom>Cart summary:</Typography>
			<Typography paragraph>{items}
			</Typography>
			
			{termsRequired ? 
			<>
			{memberSettings.terms_and_conditions_url ?
			<Typography gutterBottom>Our terms and conditions are <a href={memberSettings.terms_and_conditions_url} 
					target='_blank'
					rel="noopener noreferrer">
			available here
			</a>.</Typography>
			: null }
			
			<FormControl error={!termsAgreed} className={classes.marginBelow} variant="standard">
				<FormControlLabel
					control={
					  <Checkbox color="primary" required
					  onChange={(event) => setTermsAgreed(event.target.checked)}
					  checked={termsAgreed}
					  />
					}
					label={memberSettings.terms_and_conditions_note || 'I agree to the '+ generalSettings.formal_name +' Terms and Conditions'}
				  />
				{!termsAgreed? 
				<FormHelperText error={true}>Please confirm that you agree to the terms and conditions</FormHelperText>
				: null}
				
			</FormControl>
			
			</>
			: 
			<Typography paragraph>Confirm checkout?</Typography>
			}

			<Grid container spacing={2}>
			<Grid item>
			<Button color="primary"
				onClick={() => navigate('/cart')}
			>View cart</Button>
			</Grid>
			<Grid item>
			<Button color="primary" variant="contained"
				onClick={() => dispatchCheckout(termsAgreed)}
				disabled={termsRequired && !termsAgreed}
			>Checkout</Button>
			</Grid>
			</Grid>
			</>
			
		}
	}
	
	return (<>
	<ErrorDisplay />
	<Paper className={clsx(classes.innerContent, classes.page)} square={true}>
	<PageTitle title='Checkout' />
	{permissions.cart_available ? <CheckoutContent /> : null }
	</Paper>
	</>
)};

export default Checkout